/**
 * PlanFormModal — the create/edit modal for a PlanCatalogue tier (MANAGEMENT 14 pricing). ONE modal drives
 * both flows: `plan === null` → CREATE (POST, publish a NEW code); `plan` set → EDIT (PUT, reissue the same
 * code — correct-by-reissue, L-1). Money is edited in the major DZD unit and converted to integer centimes
 * at the wire boundary (reuse the finance money helpers, L-5). Modules + cloud SKUs are comma-separated
 * strings the operator edits inline. The `code` + `tier` are immutable on edit (the PK; a new tier is a new
 * publish) — the fields render read-only in the edit flow.
 *
 * AA: every field is a labelled <FormField>; the error is role="alert"; the submit shows a loading spinner
 * without a layout shift. FR i18n throughout; the numeric/code faces ride `.nv-ltr-island`.
 */
import { useState } from "react";
import { Button, FormField, Input, Modal, Select, Textarea, useToast } from "@/ui";
import { ApiError } from "@/api";
import { useT } from "@/app";
import { centimesToEditable, parseToCentimes, splitCsv } from "../screens/pricing.lib";
import { useCreatePlan, useUpdatePlan } from "../screens/pricing.api";
import { PLAN_TIERS, type PlanRow, type PlanTier } from "../screens/pricing.types";

export interface PlanFormModalProps {
  /** `null` while the CREATE modal is open; a row while EDIT is open; `undefined` when closed. */
  plan: PlanRow | null | undefined;
  onClose: () => void;
}

export function PlanFormModal({ plan, onClose }: PlanFormModalProps) {
  const { t } = useT();
  const toast = useToast();
  const create = useCreatePlan();
  const update = useUpdatePlan();

  const open = plan !== undefined;
  const isEdit = !!plan;

  const [code, setCode] = useState("");
  const [nom, setNom] = useState("");
  const [description, setDescription] = useState("");
  const [tier, setTier] = useState<PlanTier>("standard");
  const [prixMensuel, setPrixMensuel] = useState("");
  const [prixAnnuel, setPrixAnnuel] = useState("");
  const [modules, setModules] = useState("");
  const [cloudSku, setCloudSku] = useState("");
  const [ordre, setOrdre] = useState("");
  const [error, setError] = useState<string | null>(null);

  // Seed the form when the modal opens (one-shot per target — `null` for create keys on a stable sentinel).
  const seedKey = open ? (plan ? `edit:${plan.code}` : "create") : "_closed";
  const [seeded, setSeeded] = useState<string | null>(null);
  if (open && seeded !== seedKey) {
    setSeeded(seedKey);
    setError(null);
    if (plan) {
      setCode(plan.code);
      setNom(plan.nom);
      setDescription(plan.description ?? "");
      setTier(plan.tier);
      setPrixMensuel(centimesToEditable(plan.prixMensuelCentimes));
      setPrixAnnuel(plan.prixAnnuelCentimes != null ? centimesToEditable(plan.prixAnnuelCentimes) : "");
      setModules(plan.modules.join(", "));
      setCloudSku(plan.cloudSku.join(", "));
      setOrdre(String(plan.ordre));
    } else {
      setCode("");
      setNom("");
      setDescription("");
      setTier("standard");
      setPrixMensuel("");
      setPrixAnnuel("");
      setModules("");
      setCloudSku("");
      setOrdre("");
    }
  }
  if (!open && seeded !== null) setSeeded(null);

  const pending = create.isPending || update.isPending;

  const submit = () => {
    setError(null);
    const prixMensuelCentimes = parseToCentimes(prixMensuel);
    if (prixMensuelCentimes === null) {
      setError(t("mgmt.pricing.plan.prixInvalid"));
      return;
    }
    // Annual is optional; a non-empty-but-unparseable value is an error, an empty value → null.
    const annualTrimmed = prixAnnuel.trim();
    let prixAnnuelCentimes: number | null = null;
    if (annualTrimmed !== "") {
      const parsed = parseToCentimes(annualTrimmed);
      if (parsed === null) {
        setError(t("mgmt.pricing.plan.prixAnnuelInvalid"));
        return;
      }
      prixAnnuelCentimes = parsed;
    }
    const ordreNum = Number.parseInt(ordre, 10);
    const mods = splitCsv(modules);
    const cloud = splitCsv(cloudSku);

    if (isEdit) {
      update.mutate(
        {
          code: plan!.code,
          body: {
            nom: nom.trim(),
            description: description.trim() ? description.trim() : null,
            prix_mensuel_centimes: prixMensuelCentimes,
            prix_annuel_centimes: prixAnnuelCentimes,
            modules: mods,
            cloud_sku: cloud,
            ...(Number.isFinite(ordreNum) ? { ordre: ordreNum } : {}),
          },
        },
        {
          onSuccess: () => {
            toast.success(t("mgmt.pricing.plan.saved.edit"));
            onClose();
          },
          onError: (err) => setError(err instanceof ApiError ? err.message : t("mgmt.pricing.error")),
        },
      );
      return;
    }

    if (!code.trim()) {
      setError(t("mgmt.pricing.plan.codeRequired"));
      return;
    }
    if (!nom.trim()) {
      setError(t("mgmt.pricing.plan.nomRequired"));
      return;
    }
    create.mutate(
      {
        code: code.trim().toUpperCase(),
        nom: nom.trim(),
        description: description.trim() ? description.trim() : null,
        tier,
        prix_mensuel_centimes: prixMensuelCentimes,
        prix_annuel_centimes: prixAnnuelCentimes,
        modules: mods,
        cloud_sku: cloud,
        ...(Number.isFinite(ordreNum) ? { ordre: ordreNum } : {}),
      },
      {
        onSuccess: () => {
          toast.success(t("mgmt.pricing.plan.saved.create"));
          onClose();
        },
        onError: (err) => setError(err instanceof ApiError ? err.message : t("mgmt.pricing.error")),
      },
    );
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isEdit ? t("mgmt.pricing.plan.edit.title", { code: plan!.code }) : t("mgmt.pricing.plan.new.title")}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t("mgmt.pricing.cancel")}
          </Button>
          <Button onClick={submit} loading={pending}>
            {isEdit ? t("mgmt.pricing.plan.submit.edit") : t("mgmt.pricing.plan.submit.create")}
          </Button>
        </>
      }
    >
      <div style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-3)" }}>
        {!isEdit && (
          <FormField label={t("mgmt.pricing.plan.code")} hint={t("mgmt.pricing.plan.code.hint")} required>
            {(p) => (
              <Input
                {...p}
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder={t("mgmt.pricing.plan.code.placeholder")}
                className="nv-ltr-island"
              />
            )}
          </FormField>
        )}
        <FormField label={t("mgmt.pricing.plan.nom")} required>
          {(p) => <Input {...p} value={nom} onChange={(e) => setNom(e.target.value)} />}
        </FormField>
        <FormField label={t("mgmt.pricing.plan.tier")} required>
          {(p) => (
            <Select
              {...p}
              value={tier}
              disabled={isEdit}
              onChange={(e) => setTier(e.target.value as PlanTier)}
              options={PLAN_TIERS.map((tv) => ({ value: tv, label: t(`mgmt.pricing.tier.${tv}`) }))}
            />
          )}
        </FormField>
        <FormField label={t("mgmt.pricing.plan.description")}>
          {(p) => <Textarea {...p} value={description} onChange={(e) => setDescription(e.target.value)} rows={2} />}
        </FormField>
        <div style={{ display: "flex", gap: "var(--nv-s-3)", flexWrap: "wrap" }}>
          <div style={{ flex: "1 1 140px" }}>
            <FormField label={t("mgmt.pricing.plan.prixMensuel")} required>
              {(p) => (
                <Input
                  {...p}
                  value={prixMensuel}
                  onChange={(e) => setPrixMensuel(e.target.value)}
                  inputMode="decimal"
                  className="nv-ltr-island"
                />
              )}
            </FormField>
          </div>
          <div style={{ flex: "1 1 140px" }}>
            <FormField label={t("mgmt.pricing.plan.prixAnnuel")} hint={t("mgmt.pricing.plan.prixAnnuel.hint")}>
              {(p) => (
                <Input
                  {...p}
                  value={prixAnnuel}
                  onChange={(e) => setPrixAnnuel(e.target.value)}
                  inputMode="decimal"
                  className="nv-ltr-island"
                />
              )}
            </FormField>
          </div>
        </div>
        <FormField label={t("mgmt.pricing.plan.modules")} hint={t("mgmt.pricing.plan.modules.hint")}>
          {(p) => (
            <Input {...p} value={modules} onChange={(e) => setModules(e.target.value)} className="nv-ltr-island" />
          )}
        </FormField>
        <FormField label={t("mgmt.pricing.plan.cloudSku")} hint={t("mgmt.pricing.plan.cloudSku.hint")}>
          {(p) => (
            <Input {...p} value={cloudSku} onChange={(e) => setCloudSku(e.target.value)} className="nv-ltr-island" />
          )}
        </FormField>
        <FormField label={t("mgmt.pricing.plan.ordre")}>
          {(p) => (
            <Input
              {...p}
              value={ordre}
              onChange={(e) => setOrdre(e.target.value)}
              inputMode="numeric"
              className="nv-ltr-island"
            />
          )}
        </FormField>
        {error && (
          <p role="alert" style={{ margin: 0, color: "var(--nv-danger, var(--nv-critical))" }}>
            {error}
          </p>
        )}
      </div>
    </Modal>
  );
}
